import api from '../lib/api';

export type TimeOffType = 'VACATION' | 'SICK' | 'PERSONAL' | 'BEREAVEMENT' | 'UNPAID';
export type RequestStatus = 'PENDING' | 'APPROVED' | 'DENIED' | 'CANCELLED';

export interface PTOAllocation {
  id: string;
  agentId: string;
  agentName?: string;
  year: number;
  vacationDays: number;
  sickDays: number;
  personalDays: number;
  bereavementDays: number;
  vacationUsed: number;
  sickUsed: number;
  personalUsed: number;
  bereavementUsed: number;
  vacationRemaining: number;
  sickRemaining: number;
  personalRemaining: number;
  bereavementRemaining: number;
  totalAllocated: number;
  totalUsed: number;
  totalRemaining: number;
  createdAt: string;
  updatedAt: string;
}

export interface TimeOffRequest {
  id: string;
  agentId: string;
  agentName?: string;
  teamId?: string;
  teamName?: string;
  type: TimeOffType;
  startDate: string;
  endDate: string;
  totalDays: number;
  reason?: string;
  status: RequestStatus;
  requestedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewerName?: string;
  reviewNotes?: string;
  scheduleImpact?: ScheduleImpact;
}

export interface ShiftSwapRequest {
  id: string;
  requestingAgentId: string;
  requestingAgentName?: string;
  targetAgentId: string;
  targetAgentName?: string;
  requestingShiftId: string;
  targetShiftId: string;
  requestingShiftDate: string;
  targetShiftDate: string;
  requestingShiftTime: string;
  targetShiftTime: string;
  reason?: string;
  status: RequestStatus;
  requestedAt: string;
  targetAgentApproved?: boolean;
  targetAgentReviewedAt?: string;
  adminReviewedAt?: string;
  reviewedBy?: string;
  reviewerName?: string;
  reviewNotes?: string;
  scheduleImpact?: ScheduleImpact;
}

export interface ScheduleImpact {
  affectedShifts: number;
  coverageGap: boolean;
  understaffedHours: number;
  channelsAffected: string[];
  skillsRequired: string[];
  suggestedAlternatives?: Array<{
    agentId: string;
    agentName: string;
    availabilityMatch: number;
  }>;
}

export interface CreateTimeOffRequestDto {
  type: TimeOffType;
  startDate: string;
  endDate: string;
  reason?: string;
}

export interface CreateShiftSwapRequestDto {
  targetAgentId: string;
  requestingShiftId: string;
  targetShiftId: string;
  reason?: string;
}

export interface ReviewRequestDto {
  status: 'APPROVED' | 'DENIED';
  reviewNotes?: string;
}

class TimeOffService {
  // PTO Allocations
  async getAllocation(agentId: string, year?: number): Promise<PTOAllocation> {
    const currentYear = year || new Date().getFullYear();
    const response = await api.get(`/time-off/allocations/${agentId}?year=${currentYear}`);
    return response.data;
  }

  async updateAllocation(
    agentId: string,
    year: number,
    allocation: Partial<PTOAllocation>
  ): Promise<PTOAllocation> {
    const response = await api.patch(`/time-off/allocations/${agentId}/${year}`, allocation);
    return response.data;
  }

  // Time Off Requests
  async getMyTimeOffRequests(agentId: string): Promise<TimeOffRequest[]> {
    const response = await api.get(`/time-off/requests/agent/${agentId}`);
    return response.data;
  }

  async getAllTimeOffRequests(
    organizationId: string,
    status?: RequestStatus
  ): Promise<TimeOffRequest[]> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    const response = await api.get(`/time-off/requests?organizationId=${organizationId}&${params.toString()}`);
    return response.data;
  }

  async createTimeOffRequest(
    agentId: string,
    dto: CreateTimeOffRequestDto
  ): Promise<TimeOffRequest> {
    const response = await api.post('/time-off/requests', {
      agentId,
      ...dto,
    });
    return response.data;
  }

  async reviewTimeOffRequest(
    requestId: string,
    dto: ReviewRequestDto
  ): Promise<TimeOffRequest> {
    const response = await api.patch(`/time-off/requests/${requestId}/review`, dto);
    return response.data;
  }

  async cancelTimeOffRequest(requestId: string): Promise<TimeOffRequest> {
    const response = await api.patch(`/time-off/requests/${requestId}/cancel`);
    return response.data;
  }

  // Shift Swap Requests
  async getMyShiftSwapRequests(agentId: string): Promise<ShiftSwapRequest[]> {
    const response = await api.get(`/shift-swaps/requests/agent/${agentId}`);
    return response.data;
  }

  async getAllShiftSwapRequests(
    organizationId: string,
    status?: RequestStatus
  ): Promise<ShiftSwapRequest[]> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    const response = await api.get(`/shift-swaps/requests?organizationId=${organizationId}&${params.toString()}`);
    return response.data;
  }

  async createShiftSwapRequest(
    agentId: string,
    dto: CreateShiftSwapRequestDto
  ): Promise<ShiftSwapRequest> {
    const response = await api.post('/shift-swaps/requests', {
      requestingAgentId: agentId,
      ...dto,
    });
    return response.data;
  }

  async respondToShiftSwap(
    requestId: string,
    approved: boolean,
    notes?: string
  ): Promise<ShiftSwapRequest> {
    const response = await api.patch(`/shift-swaps/requests/${requestId}/respond`, {
      approved,
      notes,
    });
    return response.data;
  }

  async reviewShiftSwapRequest(
    requestId: string,
    dto: ReviewRequestDto
  ): Promise<ShiftSwapRequest> {
    const response = await api.patch(`/shift-swaps/requests/${requestId}/review`, dto);
    return response.data;
  }

  async cancelShiftSwapRequest(requestId: string): Promise<ShiftSwapRequest> {
    const response = await api.patch(`/shift-swaps/requests/${requestId}/cancel`);
    return response.data;
  }

  // Schedule Impact Analysis
  async analyzeTimeOffImpact(
    agentId: string,
    startDate: string,
    endDate: string
  ): Promise<ScheduleImpact> {
    const response = await api.post('/time-off/analyze-impact', {
      agentId,
      startDate,
      endDate,
    });
    return response.data;
  }

  async analyzeShiftSwapImpact(
    requestingShiftId: string,
    targetShiftId: string
  ): Promise<ScheduleImpact> {
    const response = await api.post('/shift-swaps/analyze-impact', {
      requestingShiftId,
      targetShiftId,
    });
    return response.data;
  }
}

export const timeOffService = new TimeOffService();
